// Copyright ©️ 2026 eVolve MEP, LLC

import type { WorkOrderItem, WorkOrderItemId, UpdateWorkOrderItemBody } from '@evolvemep/api-ts';
import type { ColDef, ValueGetterParams } from 'ag-grid-enterprise';

import { CellRenderer } from 'components/Mantine/CellRenderer';
import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { editableColDef } from 'helpers/ag-grid/EditableCells/editableColDef';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { destructureAndFormatQuantity } from 'helpers/formatQuantity';
import { SPOOL_UOM_ID } from 'modules/Shop/WorkOrders/WorkOrderDetail/constants';
import type { WorkOrderItemsTableAgContext } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemBOM';
import { BOMItemIcon } from 'modules/Shop/WorkOrders/WorkOrdersPage/BOMItemIcon';

import { EditItemMenu } from './EditItemMenu';

export const getWorkOrderItemBomColDef = ({
  parentItem,
  canEditItems,
  updateItem,
  onDeleteItem,
  compact,
}: {
  /** The item these rows are the bill of materials for. */
  parentItem: WorkOrderItem;
  canEditItems: boolean;
  updateItem: (workOrderItemId: WorkOrderItemId, body: UpdateWorkOrderItemBody) => Promise<WorkOrderItem>;
  onDeleteItem?: (item: WorkOrderItem) => void;
  compact?: boolean;
}): ColDef<WorkOrderItem>[] => [
  editableColDef<WorkOrderItem, 'name'>({
    colId: 'name',
    field: 'name',
    headerName: 'Item Name',
    tooltipField: 'name',
    cellRenderer: CellRenderer<WorkOrderItem, 'name'>(({ data, value }) => (
      <>
        <BOMItemIcon itemType={data.itemType} />
        {value}
      </>
    )),
    Editor: (props) => <TextInputWrapped {...props} label="Item Name" required maxLength={255} />,
    editable: canEditItems,
    // the name is required, so a cleared value falls back to the current one
    dataMapper: (item, name) => ({ name: name?.trim() || item.name }),
    flex: 1,
    minWidth: 200,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  }),
  {
    colId: 'spool-qty',
    field: 'spoolQuantity',
    headerName: 'Spool Qty',
    valueFormatter: destructureAndFormatQuantity,
    sortable: false,
    width: 100,
    hide: parentItem.unitOfMeasureId !== SPOOL_UOM_ID || compact,
    // rows under a non-spool parent have no spool quantity, so keep the column out of the tool panel — `hide` alone
    // would let a user switch on a permanently blank column
    lockVisible: parentItem.unitOfMeasureId !== SPOOL_UOM_ID,
    suppressColumnsToolPanel: parentItem.unitOfMeasureId !== SPOOL_UOM_ID,
  },
  editableColDef<WorkOrderItem, 'quantity'>({
    colId: 'unit-qty',
    field: 'quantity',
    headerName: 'Unit Qty',
    valueFormatter: destructureAndFormatQuantity,
    Editor: (props) => (
      <NumberInputWrapped
        {...props}
        label="Unit Qty"
        required
        min={1}
        max={999_999}
        clampBehavior="strict"
        allowDecimal={false}
        onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)}
      />
    ),
    editable: canEditItems,
    // the quantity is required, so a cleared value falls back to the current one
    dataMapper: (item, quantity) => ({ quantity: quantity ?? item.quantity }),
    sortable: false,
    width: 100,
  }),
  {
    colId: 'total-qty',
    headerName: 'Total Qty',
    field: 'totalQuantity',
    valueFormatter: destructureAndFormatQuantity,
    sortable: false,
    width: 100,
    hide: compact,
  },
  {
    colId: 'uom',
    headerName: 'UOM',
    valueGetter: ({ data, context }: ValueGetterParams<WorkOrderItem, unknown, WorkOrderItemsTableAgContext>) =>
      context.uoms?.find((u) => u.unitOfMeasureId === data?.unitOfMeasureId)?.code ?? null,
    sortable: false,
    width: 100,
    hide: compact,
  },
  {
    colId: 'manufacturer',
    headerName: 'Manufacturer',
    valueGetter: ({ data, context }: ValueGetterParams<WorkOrderItem, unknown, WorkOrderItemsTableAgContext>) =>
      context.manufacturers?.find((m) => m.manufacturerId === data?.manufacturerId)?.name,
    sortable: false,
    width: 180,
    hide: compact,
  },
  {
    colId: 'mfg-number',
    headerName: 'Catalog #',
    field: 'manufacturerCatalogNumber',
    sortable: false,
    width: 100,
    hide: compact,
  },
  {
    ...lockedColDef('right'),
    width: 48,
    hide: !canEditItems,
    cellRenderer: CellRenderer<WorkOrderItem>(({ api, node }) => (
      <EditItemMenu
        item={node.data}
        editable={canEditItems}
        refreshItem={() => refreshGrid(api)}
        updateItem={(data) =>
          updateItem(node.data.workOrderItemId, data).then((updatedItem) => {
            // the name column can be filtered and sorted server-side, so a rename can move the row off this page
            if (updatedItem.name !== node.data.name) {
              refreshGrid(api);
            }
            node.updateData(updatedItem);
          })
        }
        onDeleteItem={onDeleteItem}
      />
    )),
  },
];
